import { Dispatch } from 'redux';

import {
  AFC,
  DONE,
  FAILED,
  LEAGUES,
  LOADING,
  NCAA_TEAMS,
  NFC,
  NFL_TEAMS_GROUPS,
  PRACTICE,
  YEARS,
  ZEBRA_TEAMS
} from '../../config/constants';
import { SERVER } from '../../config/server.config';
import { getVenueLabel, isCC, routeToSpecificScope } from '../../helpers';
import { requestData } from '../../config/services/datasets';
import { RootState } from '../reducers';
import {
  FilterDataTypes,
  FiltersAction,
  GlobalFilterTeamsNflType,
  GlobalFilterTeamsType,
  GlobalFilterVenuesType,
  VenueType
} from '../types';

export const createSelectedFiltersProperty = (property: FilterDataTypes, filterKey: string, forceScope?: string) => {
  return (dispatch: Dispatch, getState: () => RootState) => {
    const path = getState().router.location.pathname;
    const scope = forceScope || routeToSpecificScope(path);
    dispatch({
      type: FiltersAction.CHANGE_SELECTED_FILTERS_PROPERTY,
      property,
      filterKey,
      scope,
      firstTime: true
    });
  };
};

export const changeSelectedFiltersProperty = (property: FilterDataTypes, filterKey: string, forceScope?: string) => {
  return (dispatch: Dispatch, getState: () => RootState) => {
    const path = getState().router.location.pathname;
    const scope = forceScope || routeToSpecificScope(path);
    dispatch({
      type: FiltersAction.CHANGE_SELECTED_FILTERS_PROPERTY,
      property,
      filterKey,
      scope
    });
  };
};

export const replacePracticesDataForDateRangeSelector = (dataPractices: { status: typeof DONE | typeof LOADING | typeof FAILED, practices: any[] }, teamId: string, yearSeason: number) => {
  return {
    type: FiltersAction.UPDATE_PRACTICES_DATE_RANGE_SELECTOR,
    dataPractices,
    teamId,
    yearSeason
  };
};

export const loadPracticesForDateRangeSelector = (yearSeason: number, teamId: string, dateRanges: any[]) => {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const practicesDateRangeSelector = getState().filters.PRACTICES_DATE_RANGE_SELECTOR;
    if (!(practicesDateRangeSelector &&
      practicesDateRangeSelector[teamId] &&
      practicesDateRangeSelector[teamId][yearSeason] &&
      practicesDateRangeSelector[teamId][yearSeason].status === DONE)) {
      dispatch(replacePracticesDataForDateRangeSelector({ status: LOADING, practices: [] }, teamId, yearSeason));
      const results = await requestData(SERVER.PRACTICES(teamId) + `compareRange=${dateRanges.join(',')}&teamId=${teamId}`);
      if (Array.isArray(results)) {
        dispatch(replacePracticesDataForDateRangeSelector(
          {
            status: DONE,
            practices: results.map(practice => ({
              name: practice.name,
              intensity: practice.intensity,
              gameId: practice.dsid,
              startTime: practice.startTime
            }))
          },
          teamId,
          yearSeason
        ));
      } else {
        dispatch(replacePracticesDataForDateRangeSelector({ status: FAILED, practices: [] }, teamId, yearSeason));
      }
    }
  };
};

export const getAllSeasonWeeks = () => {
  return async (dispatch: Dispatch) => {
    const weeks: { [key: number]: any } = {};
    for (const year of YEARS) {
      const results = await requestData(SERVER.NFL_WEEKS(year));
      if (results) {
        results.sort((weekA: { ts: number }, weekB: { ts: number }) => weekA.ts - weekB.ts);
      }
      weeks[year] = results;
    }
    dispatch({ type: FiltersAction.REPLACE_ALL_SEASON_WEEKS, weeks });
  };
};

// state.filters.teams
export const replaceTeams = (teams: GlobalFilterTeamsType) => {
  return {
    type: FiltersAction.REPLACE_TEAMS_LIST,
    teamsGroups: teams
  };
};

export const loadTeams = () => {
  const order = ['North', 'East', 'South', 'West'];
  return (dispatch: Dispatch, getState: () => RootState) => {
    const user = getState().user;
    const nflTeamsGroupsCopy: any = JSON.parse(JSON.stringify(NFL_TEAMS_GROUPS));
    const nflTeams: GlobalFilterTeamsNflType = { [NFC]: [], [AFC]: [] };
    nflTeams[NFC] = order.map(order => ({
      name: `${NFC} ${order}`,
      teams: nflTeamsGroupsCopy[NFC][`${NFC} ${order}`]
    }));
    nflTeams[AFC] = order.map(order => ({
      name: `${AFC} ${order}`,
      teams: nflTeamsGroupsCopy[AFC][`${AFC} ${order}`]
    }));
    dispatch(replaceTeams({ nfl: nflTeams, ncaa: isCC(user) ? [...NCAA_TEAMS, ...ZEBRA_TEAMS] : NCAA_TEAMS }));
  };
};

// state.filters.venues
export const replaceVenues = (venues: GlobalFilterVenuesType) => {
  return {
    type: FiltersAction.REPLACE_VENUES,
    venues
  };
};

export const loadVenues = (isMobile:boolean) => {
  return async (dispatch: Dispatch) => {
    const results = await requestData(SERVER.VENUES(isMobile));
    if (results) {
      const venues: GlobalFilterVenuesType = {
        ncaaFbs: {
          practice: [],
          game: []
        },
        nfl: {
          practice: [],
          game: []
        },
        mlax: {
          practice: []
        }
      };
      results.forEach((venue: VenueType) => {
        venue = { ...venue, label: getVenueLabel(venue.league, venue.type, venue.teamId) };
        if (venue.league === LEAGUES.NCAA) {
          if (venue.type === PRACTICE) {
            venues.ncaaFbs.practice.push(venue);
          } else {
            venues.ncaaFbs.game.push(venue);
          }
        } else if (venue.league === LEAGUES.NFL) {
          if (venue.type === PRACTICE) {
            venues.nfl.practice.push(venue);
          } else {
            venues.nfl.game.push(venue);
          }
        } else {
          venues.mlax.practice.push(venue);
        }
      });
      dispatch(replaceVenues(venues));
    }
  };
};
